'use client';

import {
  createGenericTelemetryEventQueue,
  type GenericTelemetryEventQueue,
} from '@/lib/telemetry-event-queue';

// App-wide client telemetry (Trello #253): browser-sourced errors, web
// vitals, slow/failed API calls, and page views, batched to
// POST /telemetry/client/events. The server stamps identity from the session;
// nothing here sends user info. Started by ClientTelemetryProvider once the
// session is authenticated, so the softphone's dedicated pipeline stays the
// deep-forensics channel and this one stays cheap and app-wide.

type ClientTelemetryQueuedEvent = {
  eventName: string;
  severity: 'info' | 'warning' | 'error';
  pagePath: string | null;
  payload: Record<string, unknown>;
  clientAt: string;
};

// Through the Next `/api` proxy like every other backend call (the rewrite
// strips the prefix; the backend route is /telemetry/client/events).
const INGEST_PATH = '/api/telemetry/client/events';
// Lower churn than the softphone's 15s uplink — these events are sparse.
const FLUSH_INTERVAL_MS = 30_000;
const API_SLOW_THRESHOLD_MS = 1_500;
const STACK_MAX_CHARS = 3_000;
// Error-loop guard: a render loop that throws per frame must not turn the
// telemetry channel into the outage. Beyond this many error events per minute
// the rest of the minute is dropped (uncounted — the burst itself is the signal).
const ERROR_EVENTS_PER_MINUTE_MAX = 10;

let started = false;
let clientSessionId: string | null = null;
let queue: GenericTelemetryEventQueue<ClientTelemetryQueuedEvent> | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersInstalled = false;
let observers: PerformanceObserver[] = [];
let errorBudgetWindowStart = 0;
let errorBudgetUsed = 0;

// Per-page-view accumulators, reported as one web_vitals summary when the
// view ends (route change or tab hidden) instead of one event per entry.
let currentPagePath: string | null = null;
let pageViewStartedAt = 0;
let lcpMs: number | null = null;
let clsValue = 0;
let maxInteractionMs = 0;
let longTaskCount = 0;
let longTaskTotalMs = 0;
let pageLoadReported = false;

function nowIso(): string {
  return new Date().toISOString();
}

async function sendBatch(events: ClientTelemetryQueuedEvent[]): Promise<boolean> {
  if (!clientSessionId) {
    return false;
  }
  try {
    // Raw fetch, not fetchJson: fetchJson reports its calls here, and the
    // telemetry uplink must never instrument itself into a loop.
    const response = await fetch(INGEST_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ clientSessionId, events }),
    });
    // A 4xx batch is poison (validation/auth) — retrying it can never
    // succeed, so treat it as delivered rather than re-queue forever.
    return response.ok || (response.status >= 400 && response.status < 500);
  } catch {
    return false;
  }
}

function ensureQueue(): GenericTelemetryEventQueue<ClientTelemetryQueuedEvent> {
  const active =
    queue ??
    createGenericTelemetryEventQueue<ClientTelemetryQueuedEvent>(sendBatch, (count) => ({
      eventName: 'client_telemetry_events_dropped',
      severity: 'warning',
      pagePath: currentPagePath,
      payload: { count },
      clientAt: nowIso(),
    }));
  queue = active;
  return active;
}

function record(
  eventName: string,
  severity: ClientTelemetryQueuedEvent['severity'],
  payload: Record<string, unknown>
): void {
  if (!started) {
    return;
  }
  ensureQueue().enqueue({
    eventName,
    severity,
    pagePath: currentPagePath,
    payload,
    clientAt: nowIso(),
  });
}

// Feature-sourced events (beyond the automatic error/vitals/API capture):
// callers must do their own throttling — this queues whatever it's handed.
// No-op until the provider has started telemetry (unauthenticated pages).
export function recordClientTelemetryEvent(
  eventName: string,
  severity: ClientTelemetryQueuedEvent['severity'],
  payload: Record<string, unknown>
): void {
  record(eventName, severity, payload);
}

// Error-severity events from callers that can fire per frame — the render
// error boundaries above all — share the window listeners' budget below, so a
// render loop cannot turn the telemetry channel into the outage.
export function recordClientErrorEvent(
  eventName: string,
  payload: Record<string, unknown>
): void {
  // Same order as the window listeners: don't spend budget before telemetry
  // has started, since nothing would be queued anyway.
  if (!started || !withinErrorBudget()) {
    return;
  }
  record(eventName, 'error', payload);
}

function withinErrorBudget(): boolean {
  const now = Date.now();
  if (now - errorBudgetWindowStart > 60_000) {
    errorBudgetWindowStart = now;
    errorBudgetUsed = 0;
  }
  errorBudgetUsed += 1;
  return errorBudgetUsed <= ERROR_EVENTS_PER_MINUTE_MAX;
}

function truncateStack(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) {
    return undefined;
  }
  return value.slice(0, STACK_MAX_CHARS);
}

function installErrorListeners(): void {
  if (listenersInstalled) {
    return;
  }
  listenersInstalled = true;

  window.addEventListener('error', (event) => {
    if (!started || !withinErrorBudget()) {
      return;
    }
    record('js_error', 'error', {
      message: String(event.message || '').slice(0, 500),
      source: `${event.filename}:${event.lineno}:${event.colno}`,
      stack: truncateStack((event.error as Error | undefined)?.stack),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!started || !withinErrorBudget()) {
      return;
    }
    const reason: unknown = event.reason;
    record('js_unhandled_rejection', 'error', {
      message:
        reason instanceof Error
          ? `${reason.name}: ${reason.message}`.slice(0, 500)
          : String(reason).slice(0, 500),
      stack: truncateStack((reason as Error | undefined)?.stack),
    });
  });

  const finalFlush = () => {
    if (!started || !queue || !clientSessionId) {
      return;
    }
    flushPageViewSummary();
    const events = queue.drainForFinalFlush();
    if (!events.length) {
      return;
    }
    const body = JSON.stringify({ clientSessionId, events });
    if (!navigator.sendBeacon(INGEST_PATH, new Blob([body], { type: 'application/json' }))) {
      void fetch(INGEST_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body,
      }).catch(() => undefined);
    }
  };
  window.addEventListener('pagehide', finalFlush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      finalFlush();
    }
  });
}

function observe(type: string, callback: (entries: PerformanceEntry[]) => void, buffered = true): void {
  try {
    const observer = new PerformanceObserver((list) => callback(list.getEntries()));
    observer.observe({ type, buffered } as PerformanceObserverInit);
    observers.push(observer);
  } catch {
    // Older browsers miss some entry types; each metric degrades independently.
  }
}

function installPerformanceObservers(): void {
  observe('largest-contentful-paint', (entries) => {
    const last = entries.at(-1);
    if (last) {
      lcpMs = Math.round(last.startTime);
    }
  });
  observe('layout-shift', (entries) => {
    for (const entry of entries) {
      const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
      if (!shift.hadRecentInput && typeof shift.value === 'number') {
        clsValue += shift.value;
      }
    }
  });
  observe('longtask', (entries) => {
    for (const entry of entries) {
      longTaskCount += 1;
      longTaskTotalMs += Math.round(entry.duration);
    }
  });
  try {
    // INP approximation: worst event-handler duration seen this page view.
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        maxInteractionMs = Math.max(maxInteractionMs, Math.round(entry.duration));
      }
    });
    observer.observe({ type: 'event', durationThreshold: 40 } as PerformanceObserverInit);
    observers.push(observer);
  } catch {
    // Same graceful degradation as above.
  }
}

function reportInitialPageLoad(): void {
  if (pageLoadReported) {
    return;
  }
  const nav = (performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]).at(0);
  if (!nav || nav.loadEventEnd <= 0) {
    return;
  }
  pageLoadReported = true;
  record('page_load', 'info', {
    ttfbMs: Math.round(nav.responseStart),
    domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd),
    loadMs: Math.round(nav.loadEventEnd),
    transferKb: Math.round((nav.transferSize || 0) / 1024),
  });
}

// One summary per page view, emitted when the view ends — volume stays
// bounded by navigation, not by how janky the page was.
function flushPageViewSummary(): void {
  if (!started || !currentPagePath || pageViewStartedAt === 0) {
    return;
  }
  const hasVitals = lcpMs !== null || clsValue > 0 || maxInteractionMs > 0 || longTaskCount > 0;
  if (hasVitals) {
    record('web_vitals', 'info', {
      lcpMs,
      cls: Math.round(clsValue * 1000) / 1000,
      maxInteractionMs,
      longTaskCount,
      longTaskTotalMs,
      viewMs: Math.round(performance.now() - pageViewStartedAt),
    });
  }
  lcpMs = null;
  clsValue = 0;
  maxInteractionMs = 0;
  longTaskCount = 0;
  longTaskTotalMs = 0;
  pageViewStartedAt = performance.now();
}

export function startClientTelemetry(): void {
  if (started || typeof window === 'undefined') {
    return;
  }
  started = true;
  clientSessionId ??= crypto.randomUUID();
  // Seed the first page view here so page_load and any early errors carry a
  // path; the provider's recordClientPageView call for the same path no-ops.
  currentPagePath = window.location.pathname;
  pageViewStartedAt = performance.now();
  installErrorListeners();
  installPerformanceObservers();
  reportInitialPageLoad();
  record('page_view', 'info', {});
  flushTimer = setInterval(() => {
    reportInitialPageLoad();
    void queue?.flush();
  }, FLUSH_INTERVAL_MS);
}

export function stopClientTelemetry(): void {
  if (!started) {
    return;
  }
  started = false;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  for (const observer of observers) {
    observer.disconnect();
  }
  observers = [];
  void queue?.flush();
}

// Called by ClientTelemetryProvider on every pathname change: closes out the
// previous view's vitals summary and opens the next one.
export function recordClientPageView(pathname: string): void {
  if (!started || pathname === currentPagePath) {
    return;
  }
  flushPageViewSummary();
  currentPagePath = pathname;
  pageViewStartedAt = performance.now();
  record('page_view', 'info', {});
}

// Route ids, not concrete ids, so the admin summary groups by endpoint.
function normalizeApiPath(path: string): string {
  return path
    .split(/[?#]/)[0]
    .replace(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g, ':id')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .slice(0, 200);
}

// When the backend is unreachable (deploy gap, dropped connection) every
// poller in every tab fails at once — one connectivity event, not one report
// per endpoint. Record at most one network error per window, none while the
// browser already knows it's offline, and expose the up/down signal so the
// shell can show a reconnect banner instead of failing silently.
const NETWORK_ERROR_DEDUPE_MS = 30_000;
let lastNetworkErrorRecordedAt = 0;
let backendDown = false;
const connectivityListeners = new Set<(down: boolean) => void>();

function setBackendDown(down: boolean): void {
  if (backendDown === down) {
    return;
  }
  backendDown = down;
  for (const listener of connectivityListeners) {
    listener(down);
  }
}

export function isBackendDown(): boolean {
  return backendDown;
}

export function subscribeBackendConnectivity(listener: (down: boolean) => void): () => void {
  connectivityListeners.add(listener);
  return () => {
    connectivityListeners.delete(listener);
  };
}

// Hooked into fetchJson (lib/api/client.ts). Only failures and slow calls are
// recorded; the happy path costs one comparison.
export function reportApiCall(input: {
  path: string;
  method: string;
  status: number | null;
  durationMs: number;
  errorMessage?: string;
}): void {
  if (input.path.startsWith(INGEST_PATH)) {
    return;
  }
  // Track connectivity even before telemetry starts (e.g. the login page) —
  // the reconnect banner should not depend on a session existing.
  if (input.status === null) {
    setBackendDown(true);
    if (!started) {
      return;
    }
    // The browser already knows it's offline — reporting adds nothing, and
    // the events queue would just replay a connectivity blip later.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }
    const now = Date.now();
    if (now - lastNetworkErrorRecordedAt < NETWORK_ERROR_DEDUPE_MS) {
      return;
    }
    lastNetworkErrorRecordedAt = now;
    record('api_network_error', 'warning', {
      path: normalizeApiPath(input.path),
      method: input.method,
      status: input.status,
      durationMs: Math.round(input.durationMs),
      message: (input.errorMessage || '').slice(0, 300),
    });
    return;
  }
  setBackendDown(false);
  if (!started) {
    return;
  }
  const payload = {
    path: normalizeApiPath(input.path),
    method: input.method,
    status: input.status,
    durationMs: Math.round(input.durationMs),
  };
  if (input.status >= 500) {
    record('api_error', 'warning', payload);
    return;
  }
  if (input.durationMs > API_SLOW_THRESHOLD_MS) {
    record('api_slow', 'info', payload);
  }
}
